/**
 * Lightweight pub/sub for the legacy API health status.
 *
 * The legacy `rental-hub-manager.emergent.host` API is out of our control;
 * when it goes down the SPA looks broken (empty lists, silent buttons).
 * Rather than adding a boolean prop to every component, we track failures
 * globally in `api.js` and let a single `<LegacyApiBanner />` (rendered in
 * the Layout) surface a non-blocking notice.
 *
 * Status values:
 *   - `"ok"`      — no consecutive failures OR recovered after a run
 *   - `"down"`    — at least `THRESHOLD` consecutive network/5xx failures
 *                   observed on the legacy client
 */

const THRESHOLD = 2; // 2 consecutive network/5xx failures → banner
let status = "ok";
let failureStreak = 0;
const listeners = new Set();

const emit = () => {
  for (const cb of listeners) {
    try { cb(status); } catch { /* isolate one bad listener */ }
  }
};

export const getLegacyApiStatus = () => status;

export const subscribeLegacyApiStatus = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

/** Called from `api.js` interceptor on any legacy response error. */
export const noteLegacyFailure = (err) => {
  // Skip auth failures — 401/403 are handled separately (login flow / OAuth)
  // and don't indicate the upstream service is unavailable.
  const s = err?.response?.status;
  const isNetworkErr = !err?.response; // no response object → network / CORS / timeout
  const isServerErr = typeof s === "number" && s >= 500 && s <= 599;
  if (!isNetworkErr && !isServerErr) {
    // Any other response (4xx, 3xx) means the service IS responding — treat
    // as recovery so the banner clears once the outage is over.
    noteLegacySuccess();
    return;
  }
  failureStreak += 1;
  if (failureStreak >= THRESHOLD && status !== "down") {
    status = "down";
    emit();
  }
};

/** Called from `api.js` interceptor on any successful legacy response. */
export const noteLegacySuccess = () => {
  failureStreak = 0;
  if (status !== "ok") {
    status = "ok";
    emit();
  }
};
